/**
 * Training Data Loader
 * 
 * Handles loading of training session data with date range filtering,
 * batch processing, and additional data loading capabilities.
 */

import { supabase } from '@/integrations/supabase/client';
import { indexedDBService } from '../../indexeddb';
import { TrainingSessionTransformer } from '../DataTransformer';
import { BaseLoader, type LoaderResult } from './BaseLoader';

/**
 * Training Data Loader Implementation
 * 
 * Specializes in loading training sessions with configurable date ranges,
 * competition session filtering, and additional data loading for extended
 * date ranges.
 */
export class TrainingDataLoader extends BaseLoader {
  /**
   * Load training sessions for a user within the configured date range
   */
  async load(userId: string): Promise<LoaderResult> {
    try {
      // Calculate date range based on configuration
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - this.config.journalDays * 24 * 60 * 60 * 1000)
        .toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('training_sessions')
        .select('*')
        .eq('user_id', userId)
        .gte('date', startDate)
        .lte('date', endDate)
        .order('date', { ascending: false });

      if (error) {
        return this.createLoaderResult(0, [error.message]);
      }

      if (data && data.length > 0) {
        const offlineSessions = data.map(TrainingSessionTransformer.fromSupabase);
        await indexedDBService.offlineDB.putMany('training_sessions', offlineSessions);
        return this.createLoaderResult(data.length);
      }

      return this.createLoaderResult(0);
    } catch (error) {
      const errorMessage = this.handleError(error, 'Training sessions loading failed');
      return this.createLoaderResult(0, [errorMessage]);
    }
  }

  /**
   * Load additional training sessions beyond the initial date range
   */
  async loadAdditional(userId: string, additionalDays: number): Promise<LoaderResult> {
    try {
      // Calculate extended date range
      const currentStartDate = new Date(Date.now() - this.config.journalDays * 24 * 60 * 60 * 1000)
        .toISOString().split('T')[0];
      const extendedStartDate = new Date(Date.now() - (this.config.journalDays + additionalDays) * 24 * 60 * 60 * 1000)
        .toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('training_sessions')
        .select('*')
        .eq('user_id', userId)
        .gte('date', extendedStartDate)
        .lt('date', currentStartDate)
        .order('date', { ascending: false });

      if (error) {
        return this.createLoaderResult(0, [error.message]);
      }

      if (data && data.length > 0) {
        const offlineSessions = data.map(TrainingSessionTransformer.fromSupabase);
        await indexedDBService.offlineDB.putMany('training_sessions', offlineSessions);
        return this.createLoaderResult(data.length);
      }

      return this.createLoaderResult(0);
    } catch (error) {
      const errorMessage = this.handleError(error, 'Additional training sessions loading failed');
      return this.createLoaderResult(0, [errorMessage]);
    }
  }

  /**
   * Load training sessions for a specific date range
   */
  async loadByDateRange(userId: string, startDate: string, endDate: string): Promise<LoaderResult> {
    try {
      const { data, error } = await supabase
        .from('training_sessions')
        .select('*')
        .eq('user_id', userId)
        .gte('date', startDate)
        .lte('date', endDate)
        .order('date', { ascending: false });

      if (error) {
        return this.createLoaderResult(0, [error.message]);
      }

      if (data && data.length > 0) {
        const offlineSessions = data.map(TrainingSessionTransformer.fromSupabase);
        await indexedDBService.offlineDB.putMany('training_sessions', offlineSessions);
        return this.createLoaderResult(data.length);
      }

      return this.createLoaderResult(0);
    } catch (error) {
      const errorMessage = this.handleError(error, 'Date range training sessions loading failed');
      return this.createLoaderResult(0, [errorMessage]);
    }
  }

  /**
   * Load recent training sessions (last N days)
   */
  async loadRecent(userId: string, days: number = 30): Promise<LoaderResult> {
    try {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000)
        .toISOString().split('T')[0];

      return await this.loadByDateRange(userId, startDate, endDate);
    } catch (error) {
      const errorMessage = this.handleError(error, 'Recent training sessions loading failed');
      return this.createLoaderResult(0, [errorMessage]);
    }
  }

  /**
   * Load competition sessions only
   */
  async loadCompetitionSessions(userId: string): Promise<LoaderResult> {
    try {
      if (!this.config.includeCompetitionSessions) {
        return this.createLoaderResult(0);
      }

      const { data, error } = await supabase
        .from('training_sessions')
        .select('*')
        .eq('user_id', userId)
        .eq('session_type', 'competition')
        .order('date', { ascending: false });

      if (error) {
        return this.createLoaderResult(0, [error.message]);
      }

      if (data && data.length > 0) {
        const offlineSessions = data.map(TrainingSessionTransformer.fromSupabase);
        await indexedDBService.offlineDB.putMany('training_sessions', offlineSessions);
        return this.createLoaderResult(data.length);
      }

      return this.createLoaderResult(0);
    } catch (error) {
      const errorMessage = this.handleError(error, 'Competition sessions loading failed');
      return this.createLoaderResult(0, [errorMessage]);
    }
  }

  /**
   * Get training session statistics
   */
  async getTrainingStats(userId: string): Promise<{
    totalSessions: number;
    competitionSessions: number;
    averageSessionsPerWeek: number;
    lastSessionDate?: string;
  }> {
    try {
      const sessions = await indexedDBService.trainingSessions.getByUserId(userId);
      
      const totalSessions = sessions.length;
      const competitionSessions = sessions.filter(s => s.session_type === 'competition').length;
      
      // Calculate average sessions per week (last 12 weeks)
      const twelveWeeksAgo = new Date(Date.now() - 12 * 7 * 24 * 60 * 60 * 1000);
      const recentSessions = sessions.filter(s => new Date(s.date) >= twelveWeeksAgo);
      const averageSessionsPerWeek = Math.round(recentSessions.length / 12 * 10) / 10;
      
      const lastSessionDate = sessions.length > 0 ? sessions[0].date : undefined;

      return {
        totalSessions,
        competitionSessions,
        averageSessionsPerWeek,
        lastSessionDate
      };
    } catch (error) {
      console.warn('Failed to calculate training stats:', error);
      return {
        totalSessions: 0,
        competitionSessions: 0,
        averageSessionsPerWeek: 0
      };
    }
  }
}

// Export singleton instance
export const trainingDataLoader = new TrainingDataLoader();
